import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import useDebouncedValue from '../hooks/useDebouncedValue';
import EmptyState from './EmptyState';
import InlineRetry from './InlineRetry';

export const MIN_SEARCH_CHARS = 2;

// Fan-out global search overlay. It is resource-agnostic: the caller passes
// `providers`, each of which knows how to query one resource and map hits to a
// common result shape ({ id, title, subtitle, scopeLabel, onOpen }). The
// component owns debouncing, parallel fetch, grouping, keyboard nav, and the
// loading/empty/error states so every resource behaves consistently.
const GlobalSearch = ({
  open,
  onClose,
  providers = [],
  context: searchContext,
  recentSearches = [],
  onRecordSearch,
  placeholder = 'Search vehicles, users, tags, societies, controllers, tickets…',
}) => {
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);

  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const ready = debouncedQuery.length >= MIN_SEARCH_CHARS;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setGroups([]);
      setLoading(false);
      setFailed(false);
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !ready) {
      setGroups([]);
      setLoading(false);
      setFailed(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setFailed(false);

    Promise.allSettled(providers.map((provider) => provider.run(debouncedQuery, searchContext))).then(
      (settled) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        const nextGroups = [];
        let rejectedCount = 0;

        settled.forEach((outcome, index) => {
          const provider = providers[index];
          if (outcome.status === 'fulfilled' && Array.isArray(outcome.value) && outcome.value.length) {
            nextGroups.push({ key: provider.key, label: provider.label, items: outcome.value });
          } else if (outcome.status === 'rejected') {
            rejectedCount += 1;
          }
        });

        setGroups(nextGroups);
        setFailed(rejectedCount > 0 && nextGroups.length === 0);
        setActiveIndex(0);
        setLoading(false);
      },
    );
  }, [open, ready, debouncedQuery, providers, searchContext, reloadToken]);

  const flatItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  const handleOpenItem = useCallback(
    (item) => {
      if (!item) {
        return;
      }
      if (onRecordSearch) {
        onRecordSearch(debouncedQuery);
      }
      if (typeof item.onOpen === 'function') {
        item.onOpen();
      }
      if (onClose) {
        onClose();
      }
    },
    [debouncedQuery, onClose, onRecordSearch],
  );

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose?.();
      return;
    }
    if (!flatItems.length) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % flatItems.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + flatItems.length) % flatItems.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      handleOpenItem(flatItems[activeIndex]);
    }
  };

  const renderBody = () => {
    if (!ready) {
      if (recentSearches.length) {
        return (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
              Recent searches
            </Typography>
            <List dense>
              {recentSearches.map((term) => (
                <ListItemButton key={term} onClick={() => setQuery(term)}>
                  <ListItemText primary={term} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        );
      }
      return (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
          Type at least {MIN_SEARCH_CHARS} characters to search across vehicles, users, tags, societies,
          controllers, and tickets.
        </Typography>
      );
    }

    if (loading) {
      return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 1, py: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Searching…
          </Typography>
        </Stack>
      );
    }

    if (failed) {
      return (
        <InlineRetry
          message="Couldn't run the search."
          onRetry={() => setReloadToken((token) => token + 1)}
        />
      );
    }

    if (!flatItems.length) {
      return (
        <EmptyState
          title="No results"
          description={`Nothing matched "${debouncedQuery}". Try a vehicle number, mobile, tag ID, or controller ID.`}
        />
      );
    }

    let runningIndex = -1;
    return (
      <Stack spacing={1}>
        {groups.map((group) => (
          <Box key={group.key}>
            <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
              {group.label}
            </Typography>
            <List dense disablePadding>
              {group.items.map((item) => {
                runningIndex += 1;
                const isActive = runningIndex === activeIndex;
                return (
                  <ListItemButton
                    key={`${group.key}-${item.id}`}
                    selected={isActive}
                    onClick={() => handleOpenItem(item)}
                  >
                    <ListItemText
                      primary={item.title}
                      secondary={[item.subtitle, item.scopeLabel].filter(Boolean).join(' · ')}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
      <Box onKeyDown={handleKeyDown} sx={{ p: 2 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          inputProps={{ 'aria-label': 'Global search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2, maxHeight: 420, overflowY: 'auto' }}>{renderBody()}</Box>
      </Box>
    </Dialog>
  );
};

export default GlobalSearch;
